import type { DiffereNavigationRequest, LibraryNavigationRequest, LibraryOpenMode, LibrarySection } from '../types/app';
import { parseDiffereDeepLink } from './differeDeepLink';
import { parseLibraryDeepLink } from './libraryDeepLink';
import { DIFFERE_CATEGORY_LABELS } from './trainingHistory';

const URL_RE = /https?:\/\/[^\s<>"']+|(?:\/[^\s<>"']*\?[^\s<>"']+)/gi;

export type ChatDeepLink =
  | { kind: 'library'; request: LibraryNavigationRequest }
  | { kind: 'differe'; request: DiffereNavigationRequest };

const LIBRARY_SECTION_LABELS: Record<LibrarySection, string> = {
  own: 'Mes parties',
  my_shares: 'Mes partages',
  shared_public: 'Partages publics',
  shared_private: 'Partage privé',
};

const LIBRARY_MODE_LABELS: Record<LibraryOpenMode, string> = {
  replay: 'Replay',
  play: 'Jouer',
  poll: 'Sondage',
};

function normalizeUrl(raw: string): string {
  return raw.replace(/[.,;:!?)]+$/g, '');
}

function tryParseDeepLink(rawUrl: string): ChatDeepLink | null {
  try {
    const base = typeof window !== 'undefined' ? window.location.origin : 'http://localhost';
    const url = new URL(rawUrl, base);
    const library = parseLibraryDeepLink(url.search);
    if (library) return { kind: 'library', request: library };
    const differe = parseDiffereDeepLink(url.search);
    if (differe) return { kind: 'differe', request: differe };
  } catch {
    // URL invalide
  }
  return null;
}

function deepLinkKey(link: ChatDeepLink): string {
  if (link.kind === 'library') return `library:${JSON.stringify(link.request)}`;
  return `differe:${JSON.stringify(link.request)}`;
}

export function parseChatDeepLinks(text: string): {
  displayText: string;
  cards: ChatDeepLink[];
} {
  if (!text) return { displayText: '', cards: [] };

  const matches = [...text.matchAll(URL_RE)];
  if (matches.length === 0) return { displayText: text, cards: [] };

  const cards: ChatDeepLink[] = [];
  const seen = new Set<string>();
  const removals: { start: number; end: number }[] = [];

  for (const match of matches) {
    const full = match[0];
    const start = match.index;
    if (start == null) continue;

    const link = tryParseDeepLink(normalizeUrl(full));
    if (!link) continue;

    removals.push({ start, end: start + full.length });
    const key = deepLinkKey(link);
    if (!seen.has(key)) {
      seen.add(key);
      cards.push(link);
    }
  }

  if (cards.length === 0) return { displayText: text, cards: [] };

  let displayText = text;
  for (let i = removals.length - 1; i >= 0; i--) {
    const { start, end } = removals[i];
    displayText = displayText.slice(0, start) + displayText.slice(end);
  }

  displayText = displayText
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();

  return { displayText, cards };
}

export function chatMessageHasDeepLinks(text: string): boolean {
  return parseChatDeepLinks(text).cards.length > 0;
}

export function formatChatDeepLinkPreview(link: ChatDeepLink): string {
  if (link.kind === 'library') {
    const { request } = link;
    if (request.pendingPollsOnly) return 'Sondages bibliothèque';
    if (request.mode === 'poll') return 'Sondage bibliothèque';
    if (request.sharedGameId) return `Partie bibliothèque #${request.sharedGameId}`;
    if (request.section) return LIBRARY_SECTION_LABELS[request.section];
    return 'Bibliothèque';
  }

  const { request } = link;
  const category = DIFFERE_CATEGORY_LABELS[request.diffType] ?? request.diffType;
  if (request.view === 'donnes') {
    const player = request.playerPseudo ?? 'joueur';
    return `Donnes différé · ${category} · ${player}`;
  }
  const parts = [`Différé · ${category}`];
  if (request.playerPseudo) parts.push(request.playerPseudo);
  if (request.dealNum != null) parts.push(`donne ${request.dealNum}`);
  return parts.join(' · ');
}

export function formatChatMessagePreview(text: string): string {
  const { displayText, cards } = parseChatDeepLinks(text);
  if (displayText) return displayText;
  if (cards.length === 1) return formatChatDeepLinkPreview(cards[0]);
  if (cards.length > 1) return `${cards.length} liens webtarot`;
  return text;
}

export function getLibraryDeepLinkPresentation(request: LibraryNavigationRequest): {
  title: string;
  subtitle: string;
  actionLabel: string;
  footer: string;
} {
  if (request.pendingPollsOnly) {
    return {
      title: 'Sondages en attente',
      subtitle: 'Bibliothèque',
      actionLabel: 'Voir les sondages',
      footer: 'Bibliothèque · Sondages',
    };
  }

  if (request.mode === 'poll' && request.sharedGameId) {
    return {
      title: 'Sondage partagé',
      subtitle: `Partie #${request.sharedGameId}`,
      actionLabel: 'Répondre au sondage',
      footer: 'Bibliothèque · Sondage',
    };
  }

  if (request.sharedGameId) {
    const modeLabel = request.mode ? LIBRARY_MODE_LABELS[request.mode] : 'Replay';
    return {
      title: request.highlightOnly ? 'Partie bibliothèque' : 'Partie partagée',
      subtitle: `Partie #${request.sharedGameId} · ${modeLabel}`,
      actionLabel: request.mode === 'play'
        ? 'Jouer la partie'
        : request.highlightOnly
          ? 'Voir dans la bibliothèque'
          : 'Voir la partie',
      footer: `Bibliothèque · ${modeLabel}`,
    };
  }

  const sectionLabel = request.section
    ? LIBRARY_SECTION_LABELS[request.section]
    : 'Bibliothèque';

  return {
    title: sectionLabel,
    subtitle: 'Ouvrir la bibliothèque',
    actionLabel: 'Ouvrir',
    footer: 'Bibliothèque',
  };
}

export function getDiffereDeepLinkPresentation(request: DiffereNavigationRequest): {
  title: string;
  subtitle: string;
  actionLabel: string;
  footer: string;
} {
  const category = DIFFERE_CATEGORY_LABELS[request.diffType] ?? request.diffType;

  if (request.view === 'donnes') {
    const player = request.playerPseudo ?? 'Joueur';
    return {
      title: 'Liste des donnes',
      subtitle: `${category} · ${player}`,
      actionLabel: 'Voir les donnes',
      footer: 'Différé · Donnes',
    };
  }

  const subtitleParts: string[] = [category];
  if (request.playerPseudo) subtitleParts.push(request.playerPseudo);
  if (request.dealNum != null) subtitleParts.push(`Donne ${request.dealNum}`);
  else if (request.partieId != null) subtitleParts.push(`Partie #${request.partieId}`);

  return {
    title: 'Donne différé',
    subtitle: subtitleParts.join(' · '),
    actionLabel: 'Voir la donne',
    footer: 'Différé · Replay',
  };
}
